import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { InfiniteScroll, Search } from 'basic-components';
import { ArrowLeft } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums, StatusesQuery } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import { Fetching, globalActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import getApiConfig from 'api/ApiConfig';

import NoResults from '../../SearchAll/ResultBlock/NoResults';
import Row from './Row';
import TagFilter from './TagFilter';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './SeeAll.scss';

const getTitle = type => {
  switch(type) {
    default:
    case StatusesEnums.STATUS_TYPE.PIPELINE: {
      return "Pipelines"
    }
    case StatusesEnums.STATUS_TYPE.ETL: {
      return "Groups"
    }
    case StatusesEnums.STATUS_TYPE.DATAPREP: {
      return "Data preps"
    }
  }
}

const REQUEST_ID = "See_All_Statuses"
const useSearchStatuses = ({ query, type, tag, getQueryBuilder, shouldBeLimited }) =>
  useInfiniteQuery({
    ...StatusesQuery.QUERIES.search(
      getQueryBuilder, 
      { 
        query: query ? `*${query}*` : "*", 
        size: CommonService.getRequestSize(32), 
        aggregate: true, 
        type, 
        tag,
        REQUEST_ID
      }
    ),
    initialPageParam: 0,
    refetchInterval: () => shouldBeLimited() ? undefined : 5000,
    getNextPageParam: CommonService.getNextPageParam
  })

const SeeAll = () => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        [tag, setTag] = useState(undefined),
        [query, setQuery] = useState(""),
        type = useSelector(state => state.statusesState.type),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useSearchStatuses({ query, type, tag, getQueryBuilder, shouldBeLimited }),
        goToHistory = useCallback((e, name) => {
          e.preventDefault()
          e.stopPropagation()
          batch(() => {
            dispatch(statusesActions.addToHistory())
            dispatch(statusesActions.changeValue(statusesActions.MODES.HISTORY, "mode"))
            dispatch(statusesActions.changeValue(name, "name"))
            dispatch(statusesActions.changeValue(type, "type"))
          })
        }, [dispatch, type]),
        onScroll = useCallback(() => fetchNextPage(), [fetchNextPage]),
        title = useMemo(() => getTitle(type), [type]),
        back = useCallback(() => dispatch(statusesActions.back()), [dispatch]),
        scrollCont = useRef()

  useEffect(() => () => {
    queryClient.cancelQueries({ queryKey: [StatusesQuery.KEYS.SEARCH, REQUEST_ID] })
  }, [queryClient])

  return (
    <div className="statuses__content statuses__see-all">
      <div className="statuses__see-all__search">
        <Search
            defaultValue={query}
            onSearch={setQuery}
            placeholder="Search pipeline statuses"
            size="large"
            variant="outline"
        />
      </div>
      <div className="statuses__see-all__content">
        <div className="statuses__see-all__content__header">
          <div className={classNames("statuses__see-all__content__header__left", { "statuses__see-all__content__header__left--with-filter": StatusesEnums.STATUS_TYPE.PIPELINE === type })}>
            <div className="statuses__see-all__content__header__left__title">
              <div 
                  className="statuses__see-all__content__header__left__title__back"
                  onClick={back}
              >
                <ArrowLeft
                    height={24}
                    width={24}
                />
              </div>
              <div className="statuses__see-all__content__header__left__title__name">
                {title}
              </div>
            </div>
            <div className="statuses__see-all__content__header__left__line"/>
          </div>
          <When condition={StatusesEnums.STATUS_TYPE.PIPELINE === type}>
            {() => (
              <TagFilter
                  setTag={setTag}
                  tag={tag}
              />
            )}
          </When>
        </div>
        <When condition={isFetchingNextPage || isPending}>
          <Fetching/>
        </When>
        <If condition={data?.pages?.length === 1 && !isPending && data?.pages?.[0]?.items.length === 0}>
          <Then>
            {() => (
              <div className="statuses__see-all__content__body__no-results">
                <NoResults/>
              </div>
            )}
          </Then>
          <Else>
            {() => (
              <div 
                  className="statuses__see-all__content__body"
                  ref={scrollCont}
              >
                <InfiniteScroll
                    className="statuses__see-all__content__body__scroll"
                    hasMore={hasNextPage}
                    initialLoad={false}
                    loadMore={onScroll}
                    threshold={800}
                    useWindow={false}
                >
                  {(data?.pages || []).map((page, pageIndex) => (
                    <Fragment key={pageIndex}>
                      {page.items.map((status, index) => (
                        <Row
                            componentStatuses={status.componentStatuses}
                            jobId={status.jobId}
                            key={status.jobId + " " + index}
                            name={type === StatusesEnums.STATUS_TYPE.ETL ? (status?.payload?.groupName || status?.name) : status?.name}
                            onHistory={e => goToHistory(e, status.name)}
                            payload={status.payload}
                            scrollCont={scrollCont}
                            startTime={status.startTime}
                            status={status.status}
                            type={status.type}
                            version={status?.payload?.version}
                            warnStatuses={status?.warnStatuses}
                        />
                      ))}
                    </Fragment>
                  ))}
                </InfiniteScroll>
              </div>
            )}
          </Else>
        </If>
      </div>
    </div>
  )
}

export default memo(SeeAll)